import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import { ProductsService } from '../../services';
import {
  ProductsState,
  Product,
  Brand,
  Category,
  CreateProductRequest,
  UpdateProductRequest,
  CreateBrandRequest,
  CreateCategoryRequest,
  BulkUploadResult,
  ProductQueryParams,
} from '../../types/api/products';

const initialState: ProductsState = {
  products: [],
  brands: [],
  categories: [],
  selectedProduct: null,
  bulkUploadResult: null,
  bulkUploading: false,
  loading: false,
  error: null,
};

export const fetchProducts = createAsyncThunk(
  'products/fetchProducts',
  async (params: ProductQueryParams = {}, { rejectWithValue }) => {
    try {
      const response = await ProductsService.getProducts(params);
      return response.results || response;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || error.message || 'Failed to fetch products');
    }
  }
);

export const fetchProduct = createAsyncThunk(
  'products/fetchProduct',
  async (id: number, { rejectWithValue }) => {
    try {
      const product = await ProductsService.getProduct(id);
      return product;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || error.message || 'Failed to fetch product');
    }
  }
);

export const createProduct = createAsyncThunk(
  'products/createProduct',
  async (data: CreateProductRequest, { rejectWithValue }) => {
    try {
      const product = await ProductsService.createProduct(data);
      return product;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || error.message || 'Failed to create product');
    }
  }
);

export const updateProduct = createAsyncThunk(
  'products/updateProduct',
  async (data: UpdateProductRequest, { rejectWithValue }) => {
    try {
      const product = await ProductsService.updateProduct(data);
      return product;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || error.message || 'Failed to update product');
    }
  }
);

export const deleteProduct = createAsyncThunk(
  'products/deleteProduct',
  async (id: number, { rejectWithValue }) => {
    try {
      await ProductsService.deleteProduct(id);
      return id;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || error.message || 'Failed to delete product');
    }
  }
);

export const bulkUploadProducts = createAsyncThunk(
  'products/bulkUploadProducts',
  async (csvFile: File, { rejectWithValue }) => {
    try {
      const result = await ProductsService.bulkUploadProducts(csvFile);
      return result;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || error.message || 'Failed to bulk upload products');
    }
  }
);

export const fetchBrands = createAsyncThunk(
  'products/fetchBrands',
  async (_, { rejectWithValue }) => {
    try {
      const brands = await ProductsService.getBrands();
      return brands;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || error.message || 'Failed to fetch brands');
    }
  }
);

export const createBrand = createAsyncThunk(
  'products/createBrand',
  async (data: CreateBrandRequest, { rejectWithValue }) => {
    try {
      const brand = await ProductsService.createBrand(data);
      return brand;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || error.message || 'Failed to create brand');
    }
  }
);

export const fetchCategories = createAsyncThunk(
  'products/fetchCategories',
  async (_, { rejectWithValue }) => {
    try {
      const categories = await ProductsService.getCategories();
      return categories;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || error.message || 'Failed to fetch categories');
    }
  }
);

export const createCategory = createAsyncThunk(
  'products/createCategory',
  async (data: CreateCategoryRequest, { rejectWithValue }) => {
    try {
      const category = await ProductsService.createCategory(data);
      return category;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || error.message || 'Failed to create category');
    }
  }
);

const productsSlice = createSlice({
  name: 'products',
  initialState,
  reducers: {
    clearError: (state) => {
      state.error = null;
    },
    setSelectedProduct: (state, action: PayloadAction<Product | null>) => {
      state.selectedProduct = action.payload;
    },
    clearBulkUploadResult: (state) => {
      state.bulkUploadResult = null;
    },
    resetProductsState: () => initialState,
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchProducts.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchProducts.fulfilled, (state, action) => {
        state.loading = false;
        state.products = action.payload;
      })
      .addCase(fetchProducts.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      });

    builder
      .addCase(fetchProduct.fulfilled, (state, action) => {
        state.selectedProduct = action.payload;
      })
      .addCase(fetchProduct.rejected, (state, action) => {
        state.error = action.payload as string;
      });

    builder
      .addCase(createProduct.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(createProduct.fulfilled, (state, action) => {
        state.loading = false;
      })
      .addCase(createProduct.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      });

    builder
      .addCase(updateProduct.fulfilled, (state, action) => {
        const index = state.products.findIndex(p => p.id === action.payload.id);
        if (index !== -1) {
          state.products[index] = action.payload;
        }
        if (state.selectedProduct?.id === action.payload.id) {
          state.selectedProduct = action.payload;
        }
      })
      .addCase(updateProduct.rejected, (state, action) => {
        state.error = action.payload as string;
      });

    builder
      .addCase(deleteProduct.fulfilled, (state, action) => {
        state.products = state.products.filter(p => p.id !== action.payload);
        if (state.selectedProduct?.id === action.payload) {
          state.selectedProduct = null;
        }
      })
      .addCase(deleteProduct.rejected, (state, action) => {
        state.error = action.payload as string;
      });

    builder
      .addCase(bulkUploadProducts.pending, (state) => {
        state.bulkUploading = true;
        state.error = null;
      })
      .addCase(bulkUploadProducts.fulfilled, (state, action) => {
        state.bulkUploading = false;
        state.bulkUploadResult = action.payload;
      })
      .addCase(bulkUploadProducts.rejected, (state, action) => {
        state.bulkUploading = false;
        state.error = action.payload as string;
      });

    builder
      .addCase(fetchBrands.fulfilled, (state, action) => {
        state.brands = action.payload;
      })
      .addCase(fetchBrands.rejected, (state, action) => {
        state.error = action.payload as string;
      });

    builder
      .addCase(createBrand.fulfilled, (state, action) => {
      })
      .addCase(createBrand.rejected, (state, action) => {
        state.error = action.payload as string;
      });

    builder
      .addCase(fetchCategories.fulfilled, (state, action) => {
        state.categories = action.payload;
      })
      .addCase(fetchCategories.rejected, (state, action) => {
        state.error = action.payload as string;
      });

    builder
      .addCase(createCategory.fulfilled, (state, action) => {
      })
      .addCase(createCategory.rejected, (state, action) => {
        state.error = action.payload as string;
      });
  },
});

export const {
  clearError,
  setSelectedProduct,
  clearBulkUploadResult,
  resetProductsState,
} = productsSlice.actions;

export default productsSlice.reducer;